/* eslint-disable react/static-property-placement */
import React, { Component } from 'react';
import createDebug from 'debug';
import styled from 'theme/styled';
import p from 'translation';
import { Button } from '@mui/material';
import FileDrop from './components/file-drop';

const debug = createDebug('evp:form:file-input');

type FileType = 'file' | 'image' | 'video';

interface FileInputProps {
  id: string;
  value?: File;
  type: FileType;
  onFile: (file: File) => void;
  uploadMessage?: JSX.Element | string;
  hideFileList?: boolean /* After upload, don't show the file list and text, show uploadMessage */;
  accept?: string;
}

export default class FileInput extends Component<FileInputProps> {
  static defaultProps = {
    type: 'image',
  };

  getTypeAsName(): string {
    switch (this.props.type) {
      case 'video':
        return p.t('components.file-input.video');
      case 'image':
        return p.t('components.file-input.image');
      default:
        return p.t('components.file-input.file');
    }
  }

  handleUploadedFile = (file: File): void => {
    debug('Handling file %o', file);
    this.props.onFile(file);
  };

  renderInput(label: JSX.Element | string): JSX.Element {
    return (
      <>
        <input
          type='file'
          accept={this.props.accept}
          id={this.props.id}
          onChange={e => {
            if (!e.target.files) return;
            this.handleUploadedFile(e.target.files[0]);
          }}
        />
        <label htmlFor={this.props.id}>
          <Button component='span' color='primary'>
            {label}
          </Button>
        </label>
      </>
    );
  }

  renderFileList(): JSX.Element {
    if (!this.props.value) return <></>;

    return (
      <Wrapper>
        <div className='selected-file'>
          <span>{this.props.value.name}</span>
          {this.renderInput(
            p.t('components.file-input.upload-another', {
              type: this.getTypeAsName(),
            }),
          )}
        </div>
      </Wrapper>
    );
  }

  renderUploadMessage(): JSX.Element {
    if (this.props.uploadMessage) {
      return <>{this.renderInput(this.props.uploadMessage)}</>;
    }

    return (
      <>
        {this.renderInput(
          p.t('components.file-input.upload-a', { type: this.getTypeAsName() }),
        )}
        <p className='drag-and-drop'>
          {p.t('components.file-input.drag-and-drop', {
            type: this.getTypeAsName(),
          })}
        </p>
      </>
    );
  }

  render(): JSX.Element {
    if (this.props.value && !this.props.hideFileList) {
      return this.renderFileList();
    }

    return (
      <Wrapper>
        <div className='zone'>
          <FileDrop
            onDrop={(files, e) => {
              debug('On file drop %o', files);
              e.preventDefault();

              if (files && files[0]) {
                this.handleUploadedFile(files[0]);
              }
            }}
          >
            {this.renderUploadMessage()}
          </FileDrop>
        </div>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  .zone {
    padding: 2em;
    border-radius: 1em;
    text-align: center;
    background: ${({ theme }) => theme.palette.grey[100]};

    label {
      color: ${({ theme }) => theme.palette.primary.main};

      :hover {
        cursor: pointer;
      }
    }

    input {
      display: none;
    }
  }

  .selected-file {
    padding: 1em 0 2em 0;

    span {
      float: left;
    }
    label {
      float: right;
    }

    input {
      display: none;
    }
  }

  @media (max-width: 600px) {
    .drag-and-drop {
      display: none;
    }
  }
`;
